import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getPersonByLicensePlateData } from '../../services/Person/personService';
import PaginationComponent from '../Pagination/PaginationComponent';

const DriverInput = ({ value, onChange, label, isInvalid, userRoles, createdNew, type, editLocked }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(value || { id: null, name: "", licensePlate: null });
  const [searchText, setSearchText] = useState(value?.name || "");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [persons, setPersons] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const previousFilterParams = useRef({});
  const [filterParams, setFilterParams] = useState({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (value?.licensePlate !== undefined) {
      setSearchText(value.licensePlate);
      setIsReadOnly(!!value?.id);
      setSelectedPerson(value);
    } else {
      setSearchText('');
      setIsReadOnly(false);
      setSelectedPerson({ id: null, name: '', licensePlate: null });
    }
  }, [value?.licensePlate, value?.id]);  

  const fetchPersons = useCallback(async () => {
    if (!shouldFetch) return;

    try {
      const data = await getPersonByLicensePlateData(filterParams, page - 1, pageSize);
      setPersons(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Erro ao buscar motoristas:", error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchPersons();
    }
  }, [page, pageSize, filterParams, fetchPersons, shouldFetch]);

  const handleKeyDown = (e) => {
    if (editLocked) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleFilter(searchText);
      setShowModal(true);
    }
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setSearchText(person.licensePlate);
    setIsReadOnly(true);
  
    onChange({
      id: person.id,
      name: person.oldId + ' - ' + person.name,
      oldId: person.oldId,
      licensePlate: person.licensePlate
    });
  
    setShowModal(false);
    setShouldFetch(false);
  };  

  const handleClear = () => {
    setSelectedPerson({ name: "", id: null, licensePlate: null });
    setSearchText("");
    setIsReadOnly(false);
    onChange(null);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };  

  const handleCloseModal = () => {
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleBlur = () => {
    if (editLocked) return;
    if (!isReadOnly && searchText?.trim()) {
      const typedPlate = searchText.trim().toUpperCase();
  
      const isValidPlate = /^[A-Z]{3}-[0-9][A-Z0-9][0-9]{2}$/.test(typedPlate);
      if (!isValidPlate) {
        return;
      }
  
      const customPerson = {
        id: null,
        name: '',
        oldId: null,
        licensePlate: typedPlate
      };
  
      setSelectedPerson(customPerson);
      onChange(customPerson);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? "is-invalid" : ""}>
          <FormControl
            placeholder="Selecione um motorista"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly || !!editLocked}
            onChange={(e) => {
              let input = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            
              let firstPart = input.slice(0, 3).replace(/[^A-Z]/g, '');
              let secondPart = input.slice(3).replace(/[^A-Z0-9]/g, '');
            
              let raw = firstPart + secondPart;
            
              if (raw.length > 7) raw = raw.slice(0, 7);
            
              let formatted = raw;
              if (raw.length > 3) {
                formatted = raw.slice(0, 3) + '-' + raw.slice(3);
              }
            
              setSearchText(formatted);
            }}                      
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles || editLocked}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Motorista</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center", display: createdNew ? "block" : "none" }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>
          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Código - Nome</th>
                <th>Placa</th>
                <th style={{ width: "50px" }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {persons.map((person) => (
                <tr key={person.id}>
                  <td>{person.oldId + ' - ' + person.name}</td>
                  <td>{person.licensePlate}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectPerson(person)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: "4px", padding: "5px 10px", minWidth: "40px", height: "30px" }}
                    >
                      <FaCheck style={{ color: "inherit", opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={persons.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DriverInput;